import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { setAIRemaining } from './useAIStatus';
import { TONES, LANGS, aiErrorMessage } from './AIGenerateDialog';

const ACTIONS = [
  { value: 'fix', label: 'Corriger les fautes' },
  { value: 'rephrase', label: 'Reformuler' },
  { value: 'shorten', label: 'Raccourcir' },
  { value: 'lengthen', label: 'Allonger' },
  { value: 'tone', label: 'Changer le ton' },
  { value: 'translate', label: 'Traduire' },
];

export const AIImproveDialog = ({ open, onOpenChange, api, field, text, onApply }) => {
  const [action, setAction] = useState('fix');
  const [tone, setTone] = useState('chaleureux');
  const [targetLang, setTargetLang] = useState('en');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/write', { mode: 'improve', field, text, action, tone, target_language: targetLang });
      setResult(res.data.text);
      setAIRemaining(res.data.remaining);
    } catch (error) {
      toast.error(aiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => { if (!v) setResult(''); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-improve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> Améliorer le texte</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {ACTIONS.map((a) => (
              <button key={a.value} type="button" onClick={() => { setAction(a.value); setResult(''); }} data-testid={`ai-action-${a.value}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${action === a.value ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary'}`}>
                {a.label}
              </button>
            ))}
          </div>
          {action === 'tone' && (
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button key={t.value} type="button" onClick={() => setTone(t.value)} data-testid={`ai-improve-tone-${t.value}`}
                  className={`px-3 py-1 rounded-full text-xs border ${tone === t.value ? 'bg-secondary text-secondary-foreground border-secondary' : 'border-border'}`}>{t.label}</button>
              ))}
            </div>
          )}
          {action === 'translate' && (
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button key={l.value} type="button" onClick={() => setTargetLang(l.value)} data-testid={`ai-improve-lang-${l.value}`}
                  className={`px-3 py-1 rounded-full text-xs border ${targetLang === l.value ? 'bg-secondary text-secondary-foreground border-secondary' : 'border-border'}`}>{l.label}</button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Avant</Label>
              <div className="p-3 rounded-lg border border-border bg-muted/30 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="ai-before-text">{text}</div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Après</Label>
              <div className="p-3 rounded-lg border border-primary/40 bg-primary/5 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto min-h-[60px]" data-testid="ai-after-text">
                {result || <span className="text-muted-foreground italic">Cliquez sur « Appliquer l'action » pour voir le résultat</span>}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={() => close(false)} data-testid="ai-improve-cancel-btn">Annuler</Button>
          <Button type="button" variant="outline" onClick={run} disabled={loading} data-testid="ai-improve-run-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}Appliquer l'action
          </Button>
          {result && (
            <Button type="button" onClick={() => { onApply(result); close(false); }} data-testid="ai-replace-btn">Remplacer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
